import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import {TagIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback} from 'react'

import type {RepoPullsBulkSelectionValue, RepoPullsBulkUpdate} from '../helpers/repo-pulls-bulk-update'

interface RepoPullsBulkLabelsProps {
  commonLabels: Array<{id: string; name: string}>
  disabled: boolean
  nested: boolean
  owner: string
  repo: string
  onUpdate: (update: RepoPullsBulkUpdate) => void
}

function accessibleAnchorProps(anchorProps: React.HTMLAttributes<HTMLElement>, disabled: boolean) {
  return disabled
    ? {...anchorProps, 'aria-disabled': true, onClick: (event: React.MouseEvent<HTMLElement>) => event.preventDefault()}
    : anchorProps
}

function labelDatabaseId(label: LabelPickerLabel): string | undefined {
  return label.fullDatabaseId ?? (label.databaseId == null ? undefined : String(label.databaseId))
}

export function RepoPullsBulkLabels({commonLabels, disabled, nested, owner, repo, onUpdate}: RepoPullsBulkLabelsProps) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (nested) {
        return (
          <ActionList.Item {...accessibleAnchorProps(anchorProps, disabled)} role="menuitem">
            <ActionList.LeadingVisual>
              <TagIcon />
            </ActionList.LeadingVisual>
            Label
          </ActionList.Item>
        )
      }

      return (
        <Button
          leadingVisual={TagIcon}
          trailingVisual={TriangleDownIcon}
          {...accessibleAnchorProps(anchorProps, disabled)}
        >
          Label
        </Button>
      )
    },
    [disabled, nested],
  )

  const handleSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const commonLabelIdsByName = new Map(commonLabels.map(label => [label.name, label.id]))
      const labels: Record<string, RepoPullsBulkSelectionValue> = Object.fromEntries(
        selectedLabels.flatMap(label => {
          const id = labelDatabaseId(label)
          return id && !commonLabelIdsByName.has(label.name) ? [[id, '1']] : []
        }),
      )

      for (const label of selectedLabels) commonLabelIdsByName.delete(label.name)
      for (const id of commonLabelIdsByName.values()) {
        labels[id] = '0'
      }

      if (Object.keys(labels).length > 0) onUpdate({labels})
    },
    [commonLabels, onUpdate],
  )

  return (
    <LabelPicker
      anchorElement={anchorElement}
      title="Apply labels"
      showEditLabelsButton={false}
      repo={repo}
      owner={owner}
      readonly={disabled}
      onSelectionChange={handleSelectionChange}
      labels={[]}
      labelNames={commonLabels.length > 0 ? commonLabels.map(label => label.name) : undefined}
      canCreateLabel={false}
      nested={nested}
    />
  )
}
